// src/features/wbs/WbsList.tsx
import React, { useMemo, useState, useCallback } from 'react';
// import { upsertDraftByNodeId } from 'src/lib/wbs/progressService';
import type { WeekFlag } from './types';
// import { POLICY, type WbsFlatItem, type WbsListMode } from './types';
import type { WbsFlatItem } from './types';
import StatusBadge from './components/common/StatusBadge';
import { statusFromRow } from './components/common/statusFromRow';
import WbsProgressChip from './components/common/WbsProgressChip';
import { parseISO } from '../../utils/date';
import Modal from '../../components/common/ui/Modal';
import { useColumnResize } from '../../hooks/useColumnResize';
import { Link } from 'react-router-dom';
import { useProjectAccessCtx } from 'src/features/projects/ProjectAccessContext';
import { hasPerm } from 'src/features/projects/hooks/useProjectAccess';
import WbsProgressHistoryModal from './WbsProgressHistoryModal';
// import AssignmentToolbar from './components/AssignmentToolbar';
import AssignResponsibleCompanyModal from './components/AssignResponsibleCompanyModal';
import type { WbsEvViewMode } from './components/overview/AsOfSelector';
import {
    buildWeightedValueMap,
    getCompletedCarryForWeek,
    getComparisonValue,
    getWeekMinusOneColumnLabel,
    hasWeekMinusOnePending,
} from './lib/weeklyEvDisplay';
import { buildCurrentWeekDisplayMap } from './lib/liveEv';

type TreeIndex = {
    childrenMap: Record<string, string[]>;
    parentMap: Record<string, string | null>;
    parentsWithChildren: Set<string>;
};

function buildTreeIndex(rows: WbsFlatItem[]): TreeIndex {
    const childrenMap: Record<string, string[]> = {};
    const parentMap: Record<string, string | null> = {};
    const parentsWithChildren = new Set<string>();
    for (const r of rows) {
        parentMap[r.id] = r.parentId ?? null;
        if (r.parentId != null) {
            if (!childrenMap[r.parentId]) childrenMap[r.parentId] = [];
            childrenMap[r.parentId].push(r.id);
            parentsWithChildren.add(r.parentId);
        }
    }
    return { childrenMap, parentMap, parentsWithChildren };
}

function computeVisibleRows(
    rows: WbsFlatItem[],
    expanded: Set<string>, // [STR-ID]
    idx: TreeIndex,
): WbsFlatItem[] {
    const visible: WbsFlatItem[] = [];
    const idToRow = new Map<string, WbsFlatItem>(rows.map((r) => [r.id, r])); // [STR-ID]
    const roots = rows.filter((r) => r.parentId == null);
    const walk = (node: WbsFlatItem) => {
        visible.push(node);
        const kids = idx.childrenMap[node.id] || [];
        if (!expanded.has(node.id)) return;
        for (const childId of kids) {
            const child = idToRow.get(childId);
            if (child) walk(child);
        }
    };
    for (const root of roots) walk(root);
    return visible;
}

// function normalizeEvInput(s: string) {
//     const t = (s ?? '').trim();
//     if (t === '') return ''; // 빈칸은 빈칸으로
//     const n = Number(t);
//     if (!Number.isFinite(n)) return t; // 숫자 아닌 입력은 그대로(또는 "" 처리)
//     return String(n); // "10", "10.0", "010" -> "10"
// }

function formatPct(n: number | null | undefined) {
    return n == null || !Number.isFinite(n) ? '-' : `${Number(n).toFixed(1)}`;
}

function formatLiveProvisional({
    provisional,
    confirmed,
    plannedStarted,
}: {
    provisional: number | null;
    confirmed: number | null;
    plannedStarted: boolean;
}) {
    if (provisional != null && Number.isFinite(provisional)) {
        return `${Number(provisional).toFixed(1)}`;
    }
    if (confirmed != null && confirmed >= 100) {
        return `${Number(confirmed).toFixed(1)}`;
        // return '완료'
    }
    if (!plannedStarted) {
        return '-';
    }
    return '미입력';
}

// function getConfirmedColumnLabel(evViewMode: WbsEvViewMode) {
//     return evViewMode === 'LIVE' ? '확정(지난주)' : '확정';
// }

function shouldShowWeeklyInput(evViewMode: WbsEvViewMode) {
    return evViewMode === 'LIVE';
}

/* ──────────────────────────────────────────────────────────────
 * 메인 리스트
 * ──────────────────────────────────────────────────────────── */
export function WbsList({
    projectId,
    // mode = 'viewer',
    // canEdit = false,
    canAssign = false,
    rows,
    rollupRows,
    // onChangeRows,
    // onSaveRows,
    pvRefDate,
    refFriday,
    flags,
    // onRefreshFlags,
    counts,
    onOpenLinks,
    versions,
    currentListVersion,
    selectedVersion,
    onChangeVersion,
    // actorProjectCompanyId,
    filterSummary,
    onResetFilters,
    // wbsListId,
    onAssigned,
    weeklyProgressComparison,
    approvedMapW1,
    approvedMapW2,
    // entryClosed,
    // entryAutoApproveAt,
    // compact,
    evViewMode = 'CONFIRMED_W1',
    // liveAvailable = false,
}: {
    projectId: string; // [STR-ID] 프로젝트 ID도 string
    // mode?: WbsListMode;
    // canEdit?: boolean;
    canAssign: boolean;
    rows: WbsFlatItem[]; // [STR-ID] WbsFlatItem.id/parentId는 string 기반이어야 함
    rollupRows?: WbsFlatItem[];
    // onChangeRows: (next: WbsFlatItem[]) => void;
    // onSaveRows: (next: WbsFlatItem[]) => void;
    pvRefDate: Date;
    refFriday: Date;
    flags?: Record<string, WeekFlag>; // [STR-ID] key를 string으로
    // onRefreshFlags: () => Promise<void> | void;
    counts?: Record<string, { tasks?: number; risks?: number }>; // [STR-ID] key string
    onOpenLinks?: (mode: 'risk' | 'task', item: { id: string; nodeId: string; code: string; name: string }) => void; // [STR-ID]
    versions: Array<{ version: number; isCurrent: boolean; createdAt?: string; activatedAt?: string }>;
    currentListVersion?: number;
    selectedVersion?: number;
    onChangeVersion?: (v: number) => void;
    // actorProjectCompanyId?: string | null;
    filterSummary?: string[];
    onResetFilters?: () => void;
    // wbsListId?: string;
    onAssigned?: () => Promise<void> | void;
    weeklyProgressComparison?: any | null;
    approvedMapW1?: Record<string, { approvedValue: number; sourceWeekOf: string }>;
    approvedMapW2?: Record<string, { approvedValue: number; sourceWeekOf: string }>;
    // entryClosed?: boolean;
    // entryAutoApproveAt?: Date;
    // compact?: boolean;
    evViewMode?: WbsEvViewMode;
    // liveAvailable?: boolean;
}) {
    // WBS 편집 ACCESS 관리
    const { access, isLoading: accessLoading } = useProjectAccessCtx();
    const canEditWBS = !accessLoading && hasPerm(access?.permissions, 'wbs.edit');

    // const policy = POLICY[mode];
    // const isReadOnly = !canEdit;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const F = flags ?? {};

    /* Column Resize */
    const nameCol = useColumnResize('name', 320, 180, 720);

    /* 선택 상태
     * - progress 모드: 행(row id) 단위 선택 후 초안 저장
     */
    // const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set()); // [STR-ID]
    const [assignModalOpen, setAssignModalOpen] = useState(false);

    // const baselineEvRef = useRef<Map<string, string>>(new Map());
    // useEffect(() => {
    //     const m = new Map<string, string>();
    //     for (const r of rows) {
    //         // leaf의 원래값 기준: earnedValue(=actualProgress) 또는 실제 actualProgress 필드
    //         // 여기서는 earnedValue를 기준으로 예신
    //         const v = r.earnedValue;
    //         m.set(r.id, normalizeEvInput(v == null ? '' : String(v)));
    //     }
    //     baselineEvRef.current = m;
    // }, [rows]);

    /* 상세/반려 모달 */
    const [historyModal, setHistoryModal] = useState<{
        open: boolean;
        row: WbsFlatItem | null;
        pv: number | null;
        baseDate: Date | null;
    }>({ open: false, row: null, pv: null, baseDate: null });
    const [noteModal, setNoteModal] = useState<{ open: boolean; note: string | null }>({ open: false, note: null });

    /* 메타 저장 상태(에디터용) */
    // const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // const [dirtyMeta, setDirtyMeta] = useState(false);

    /* progress 입력 버퍼 */
    // const [evInput, setEvInput] = useState<Record<string, string>>({}); // [STR-ID]
    // const [dirtyIds, setDirtyIds] = useState<Set<string>>(new Set()); // [STR-ID]

    /* 트리 상태 */
    // const treeIndex = useMemo(() => buildTreeIndex(rows), [rows]);
    const displayTreeIndex = useMemo(() => buildTreeIndex(rows), [rows]);

    /* rollup 트리(실적 %, EV 집계) : 전체 기준 */
    const rollupBaseRows = rollupRows ?? rows;
    const rollupTreeIndex = useMemo(() => buildTreeIndex(rollupBaseRows), [rollupBaseRows]);

    const aggregatedEvMap = useMemo(() => {
        const idToRow = new Map(rollupBaseRows.map((r) => [r.id, r] as const));
        const memo = new Map<string, { wSum: number; wTot: number; hasAny: boolean }>();

        const dfs = (id: string): { wSum: number; wTot: number; hasAny: boolean } => {
            const cached = memo.get(id);
            if (cached) return cached;

            const row = idToRow.get(id);
            const kids = rollupTreeIndex.childrenMap[id] ?? [];

            // leaf
            if (!kids.length) {
                const ev = row?.earnedValue;
                const w = row?.actualWeight ?? 0; // 0~100
                const hasAny = ev != null && Number.isFinite(ev) && w > 0;
                const res = hasAny
                    ? { wSum: Number(ev) * w, wTot: w, hasAny: true }
                    : { wSum: 0, wTot: 0, hasAny: false };
                memo.set(id, res);
                return res;
            }

            // parent: children roll-up
            let wSum = 0;
            let wTot = 0;
            let hasAny = false;

            for (const cid of kids) {
                const r = dfs(cid);
                wSum += r.wSum;
                wTot += r.wTot;
                if (r.hasAny) hasAny = true;
            }

            const res = { wSum, wTot, hasAny };
            memo.set(id, res);
            return res;
        };

        const out: Record<string, { pct: number | null; hasAny: boolean; wTot: number }> = {};
        // ✅ out은 “화면 rows”에 대해서만 만들면 충분(렌더 대상만)
        for (const r of rows) {
            const a = dfs(r.id);
            out[r.id] = {
                pct: a.hasAny && a.wTot > 0 ? a.wSum / a.wTot : null,
                hasAny: a.hasAny,
                wTot: a.wTot,
            };
        }
        return out;
    }, [rows, rollupBaseRows, rollupTreeIndex.childrenMap]);

    const LS_EXPanded = `wbs:expanded:${projectId}`;
    const [expanded, setExpanded] = useState<Set<string>>(() => {
        // [STR-ID]
        try {
            const raw = localStorage.getItem(LS_EXPanded);
            if (raw) return new Set<string>(JSON.parse(raw));
        } catch {}
        return new Set<string>(Array.from(buildTreeIndex(rows).parentsWithChildren.values()));
    });

    const persistExpanded = useCallback(
        (next: Set<string>) => {
            // [STR-ID]
            try {
                localStorage.setItem(LS_EXPanded, JSON.stringify(Array.from(next)));
            } catch {}
        },
        [LS_EXPanded],
    );

    const toggleExpand = useCallback(
        (id: string) => {
            // [STR-ID]
            setExpanded((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                persistExpanded(next);
                return next;
            });
        },
        [persistExpanded],
    );

    const expandAll = useCallback(() => {
        const next = new Set<string>(displayTreeIndex.parentsWithChildren); // [STR-ID]
        setExpanded(next);
        persistExpanded(next);
    }, [displayTreeIndex.parentsWithChildren, persistExpanded]);

    const collapseAll = useCallback(() => {
        setExpanded(new Set());
        persistExpanded(new Set());
    }, [persistExpanded]);

    // useEffect(() => {
    //     if (!compact) return;
    //     expandAll();
    // }, [compact, expandAll]);

    const isAllExpaned = useMemo(() => {
        const expandable = Array.from(displayTreeIndex.parentsWithChildren);
        if (expandable.length === 0) return true;
        return expandable.every((id) => expanded.has(id));
    }, [expanded, displayTreeIndex.parentsWithChildren]);

    const toggleAllExpanded = useCallback(() => {
        if (isAllExpaned) collapseAll();
        else expandAll();
    }, [collapseAll, expandAll, isAllExpaned]);

    // const idx = useMemo(() => buildTreeIndex(rows), [rows]);
    const visibleRows = useMemo(
        () => computeVisibleRows(rows, expanded, displayTreeIndex),
        [rows, expanded, displayTreeIndex],
    );

    /* progress 입력 초기화: 기존 확정 EV 또는 이번 주 초안 EV를 버퍼에 채움 */
    // useEffect(() => {
    //     if (!rows.length) return;
    //     const buf: Record<string, string> = {}; // [STR-ID]
    //     for (const r of rows) {
    //         const f = F[r.id];
    //         if (f?.draftEV != null) {
    //             buf[r.id] = String(f.draftEV);
    //         } else if (r.earnedValue != null) {
    //             buf[r.id] = String(r.earnedValue);
    //         } else {
    //             buf[r.id] = '';
    //         }
    //     }
    //     setEvInput(buf);
    //     setDirtyIds(new Set());
    //     setSelectedIds(new Set());
    // }, [rows, F, projectId, refFriday]);

    /* --------------- 선택 & 일괄 토글 --------------- */
    // const toggleSelect = (id: string, checked: boolean) => {
    //     // [STR-ID]
    //     setSelectedIds((prev) => {
    //         const n = new Set(prev);
    //         checked ? n.add(id) : n.delete(id);
    //         return n;
    //     });
    // };
    // progress 모드: 표시 중 행 전체 토글
    // const toggleSelectAllVisibleProgress = (checked: boolean) => {
    //     setSelectedIds(() => {
    //         const n = new Set<string>();
    //         if (checked) {
    //             visibleRows.forEach((r) => {
    //                 const hasChildren = (rollupTreeIndex.childrenMap[r.id]?.length ?? 0) > 0;
    //                 if (canEdit && !hasChildren) n.add(r.id);
    //             });
    //         }
    //         return n;
    //     });
    // };

    // const rowById = useMemo(() => {
    //     const m = new Map<string, (typeof rows)[number]>();
    //     rows.forEach((r) => m.set(r.id, r));
    //     return m;
    // }, [rows]);

    /* --------------- 초안 저장(progress) / 메타 저장(editor) --------------- */
    // const canResubmitAfterClose = (nodeId: string) => {
    //     if (!entryClosed) return true;
    //     const f = F?.[nodeId];
    //     if (f?.statusForWeek !== 'REJECTED') return false;
    //     if (!entryAutoApproveAt) return false;

    //     return new Date() < entryAutoApproveAt;
    // };

    // const saveSelected = async () => {
    //     const blocked = Array.from(selectedIds).filter((id) => !canResubmitAfterClose(id));
    //     if (blocked.length > 0) {
    //         setError('입력 마감 이후에는 반려(REJECTED)된 항목만 자동승인 전까지 재 제출할 수 있습니다.');
    //         return;
    //     }

    //     if (entryClosed) {
    //         setError('입력 마감(자정) 이후에는 저장할 수 없습니다. PM 승인 단계입니다.');
    //         return;
    //     }

    //     setError(null);
    //     setSaving(true);

    //     try {
    //         // 표시 중 + 수정된 행만 저장
    //         const visibleIds = new Set(visibleRows.map((r) => r.id)); // [STR-ID]
    //         const idsToSave = Array.from(selectedIds)
    //             .filter((id) => F?.[id]?.statusForWeek !== 'APPROVED')
    //             // .filter((id) => dirtyIds.has(id) && visibleIds.has(id));
    //             .filter((id) => visibleIds.has(id));

    //         if (!idsToSave.length) return;

    //         for (const id of idsToSave) {
    //             const raw = evInput[id];
    //             const num = Number(raw);
    //             if (!Number.isFinite(num) || num < 0 || num > 100) continue;

    //             const nodeId = rowById.get(id)?.nodeId; // [버전 ID -> 노드 ID]
    //             if (!nodeId) continue;

    //             try {
    //                 await upsertDraftByNodeId(projectId, refFriday, nodeId, num, currentListVersion);
    //             } catch (e: any) {
    //                 const status = e?.response?.status;
    //                 const code = e?.response?.data?.error?.code; // ✅ 중요
    //                 const msg = e?.response?.data?.error?.message; // ✅ 중요

    //                 if (status === 409) {
    //                     if (code === 'RESUBMISSION_NOT_ALLOWED') {
    //                         setError(
    //                             '이미 승인(확정)된 주차로 잠금 상태입니다. PM이 반려(reject)해야 수정할 수 있습니다.',
    //                         );
    //                         return;
    //                     }
    //                     if (code === 'AMBIGUOUS_COMPANY_CONTEXT') {
    //                         setError(
    //                             '진척 입력 회사 컨텍스트가 모호합니다(프로젝트 내 여러 회사 소속). 회사 컨텍스트 선택 기능이 필요합니다.',
    //                         );
    //                         return;
    //                     }
    //                     setError(msg ?? '요청 충돌(409)이 발생했습니다.');
    //                     return;
    //                 }

    //                 // 403 등도 메시지 명확히
    //                 if (status === 403) {
    //                     setError(msg ?? '권한이 없습니다(403).');
    //                     return;
    //                 }

    //                 setError(msg ?? e?.message ?? '저장 중 오류가 발생했습니다.');
    //                 return;
    //             }
    //         }

    //         await onRefreshFlags?.();

    //         setDirtyIds((prev) => {
    //             const n = new Set(prev);
    //             idsToSave.forEach((i) => n.delete(i));
    //             return n;
    //         });

    //         setSelectedIds((prev) => {
    //             const n = new Set(prev);
    //             idsToSave.forEach((i) => n.delete(i));
    //             return n;
    //         });
    //     } finally {
    //         setSaving(false);
    //     }
    // };

    /* --------------- 헤더 컬럼/셀 개수 --------------- */
    const showWeek1Column = evViewMode !== 'CONFIRMED_W2';
    const showCurrentColumn = evViewMode === 'LIVE';
    const showWeeklyInput = shouldShowWeeklyInput(evViewMode);
    const week1Pending = useMemo(() => hasWeekMinusOnePending(weeklyProgressComparison), [weeklyProgressComparison]);
    const week1ColumnLabel = useMemo(
        () => getWeekMinusOneColumnLabel(weeklyProgressComparison),
        [weeklyProgressComparison],
    );
    const week2ConfirmedMap = useMemo(
        () =>
            buildWeightedValueMap(
                rows,
                (row) => {
                    const completedCarry = getCompletedCarryForWeek(row, new Date(refFriday.getTime() - 14 * 86400000));
                    return (
                        approvedMapW2?.[row.nodeId]?.approvedValue ??
                        getComparisonValue(weeklyProgressComparison?.n2?.approved?.[row.nodeId]) ??
                        completedCarry
                    );
                },
            ),
        [approvedMapW2, refFriday, rows, weeklyProgressComparison],
    );
    const week1ApprovedMap = useMemo(
        () =>
            buildWeightedValueMap(
                rows,
                (row) => {
                    const completedCarry = getCompletedCarryForWeek(row, new Date(refFriday.getTime() - 7 * 86400000));
                    return (
                        approvedMapW1?.[row.nodeId]?.approvedValue ??
                        getComparisonValue(weeklyProgressComparison?.n1?.approved?.[row.nodeId]) ??
                        completedCarry
                    );
                },
            ),
        [approvedMapW1, refFriday, rows, weeklyProgressComparison],
    );
    const week1DisplayMap = useMemo(
        () =>
            buildWeightedValueMap(rows, (row) => {
                const completedCarry = getCompletedCarryForWeek(row, new Date(refFriday.getTime() - 7 * 86400000));
                const approved = getComparisonValue(weeklyProgressComparison?.n1?.approved?.[row.nodeId]);
                const provisional = getComparisonValue(weeklyProgressComparison?.n1?.provisional?.[row.nodeId]);
                return provisional ?? approvedMapW1?.[row.nodeId]?.approvedValue ?? approved ?? completedCarry;
            }),
        [approvedMapW1, refFriday, rows, weeklyProgressComparison],
    );
    const currentWeekMap = useMemo(
        () => buildCurrentWeekDisplayMap(rows, F, approvedMapW1 ?? {}),
        [F, approvedMapW1, rows],
    );

    // const COL_COUNT = compact ? (showWeeklyInput ? 10 : 9) : showLiveColumns ? (showWeeklyInput ? 14 : 13) : 11;
    const COL_COUNT = 10 + (showWeek1Column ? 1 : 0) + (showCurrentColumn ? 1 : 0) + (showWeeklyInput ? 1 : 0);

    return (
        <section className="overflow-x-auto rounded-2xl border bg-white overflow-hidden">
            {/* 헤더 + 액션 */}
            <div className="border-b px-4 py-3">
                <div className="w-full flex items-center justify-between">
                    <div className="flex items-center justify-between gap-20">
                        <div>
                            <h2 className="text-base font-semibold">WBS Node</h2>
                        </div>
                        {/* 상단 바: 현재 버전 표시 + 선택 */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">Current List Version:</span>
                                <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-800 text-sm">
                                    {currentListVersion ? `V${currentListVersion}` : '-'}
                                </span>

                                <span className="ml-4 text-sm text-gray-600">View Version:</span>
                                <select
                                    value={selectedVersion ?? ''}
                                    // onChange={(e) => onChangeVersion?.(Number(e.target.value))}
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        if (raw === '') return;
                                        const v = Number(raw);
                                        if (!Number.isInteger(v) || v <= 0) return;
                                        onChangeVersion?.(v);
                                    }}
                                    className="border rounded px-2 py-1 text-sm"
                                >
                                    {(versions ?? []).length === 0 ? (
                                        <option value="" disabled>
                                            no versions
                                        </option>
                                    ) : (
                                        versions
                                            .sort((a, b) => b.version - a.version)
                                            .map((v) => (
                                                <option key={v.version} value={v.version}>
                                                    V{v.version}
                                                    {v.isCurrent ? ' (current)' : ''}
                                                </option>
                                            ))
                                    )}
                                </select>

                                {selectedVersion != null &&
                                    currentListVersion != null &&
                                    selectedVersion !== currentListVersion && (
                                        <span className="ml-2 text-xs text-amber-700">
                                            Viewing past version (read-only)
                                        </span>
                                    )}
                            </div>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                            <button
                                type="button"
                                className="rounded border px-2 py-1 hover:bg-gray-50"
                                onClick={toggleAllExpanded}
                                title={isAllExpaned ? '전체 접기' : '전체 펼치기'}
                            >
                                {isAllExpaned ? '전체 접기' : '전체 펼치기'}
                            </button>
                        </div>
                        {/* 필터 */}
                        <div className="flex items-center gap-2">
                            {filterSummary?.length ? (
                                <div className="flex items-center gap-2">
                                    <div className="flex flex-wrap gap-1">
                                        {filterSummary.slice(0, 6).map((t) => (
                                            <span
                                                key={t}
                                                className="text-[11px] rounded-full px-2 py-0.5 bg-gray-100 text-gray-700"
                                            >
                                                {t}
                                            </span>
                                        ))}
                                        {filterSummary.length > 6 && (
                                            <span className="text-[11px] rounded-full px-2 py-0.5 bg-gray-100 text-gray-700">
                                                +{filterSummary.length - 6}
                                            </span>
                                        )}
                                    </div>

                                    {onResetFilters && (
                                        <button
                                            type="button"
                                            className="text-xs px-2 py-1 rounded-md border hover:bg-gray-50"
                                            onClick={onResetFilters}
                                        >
                                            필터 초기화
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="text-xs text-gray-400">필터 없음</div>
                            )}
                        </div>
                    </div>

                    {canAssign && (
                        <button
                            type="button"
                            className="rounded border px-3 py-1 hover:bg-gray-50 text-xs flex flex-col items-start"
                            onClick={() => setAssignModalOpen(true)}
                            title="Assign Responsible Company"
                        >
                            <span className="leading-none">Assign Responsible Company</span>
                            <span className="text-[10px] text-gray-500 leading-none mt-0.5">담당회사 배정</span>
                        </button>
                    )}

                    {assignModalOpen && (
                        <AssignResponsibleCompanyModal
                            projectId={projectId}
                            // wbsListId={wbsListId ?? ''}
                            rows={rows}
                            onClose={() => setAssignModalOpen(false)}
                            onPublished={() => {
                                setAssignModalOpen(false);
                                onAssigned?.(); // 기존 refresh 콜백 재사용
                            }}
                        />
                    )}

                    {/* {canEdit && (
                        <div className="flex items-center gap-2">
                            {canEdit && !entryClosed && (
                                <button
                                    onClick={saveSelected}
                                    disabled={
                                        entryClosed ||
                                        saving ||
                                        // Array.from(selectedIds).filter((id) => dirtyIds.has(id)).length === 0
                                        selectedIds.size === 0
                                    }
                                    className="text-xs rounded-lg px-2.5 py-1.5 text-white bg-gray-900 hover:bg-gray-700 disabled:opacity-50"
                                    title="선택한 행의 EV 초안을 저장(제출)"
                                >
                                    {saving ? 'Saving...' : 'Save changes (selected)'}
                                </button>
                            )}
                        </div>
                    )} */}

                    {/* WBS 편집 버튼 // TODO: 권한있는 경우에만 보이도록 수정할 것. */}
                    {canEditWBS ? (
                        <Link to={`/projects/${projectId}/wbs/editor`}>
                            <button
                                type="button"
                                className="rounded border px-3 py-1 hover:bg-gray-50 text-xs"
                                title="WBS 편집창으로 이동"
                            >
                                WBS 편집
                            </button>
                        </Link>
                    ) : (
                        <></>
                    )}
                </div>
            </div>

            {/* 에러 메시지 */}
            {error && (
                <div className="mx-4 mb-2 rounded-md border border-rose-300 bg-rose-50 text-rose-800 text-sm px-3 py-2">
                    <div className="flex items-start justify-between gap-3">
                        <div>{error}</div>
                        <button
                            type="button"
                            className="shrink-0 rounded px-2 py-1 text-xs hover:bg-red-100"
                            onClick={() => setError(null)}
                            aria-label="close error"
                        >
                            X
                        </button>
                    </div>
                </div>
            )}

            {/* 테이블 */}
            <table className="min-w-full //font-mono text-sm">
                <thead>
                    <tr className="text-left text-gray-500">
                        {/* 선택 */}
                        {/* <th className="px-3 py-2 w-10">
                            {canEdit && (
                                <input
                                    type="checkbox"
                                    className="accent-gray-700"
                                    onChange={(e) => {
                                        toggleSelectAllVisibleProgress(e.target.checked);
                                    }}
                                    title="표시중 전체 선택/해제"
                                />
                            )}
                        </th> */}
                        <th className="px-3 py-2 hidden sm:table-cell">코드</th>

                        <th className="px-0 py-2 w-[10%]">
                            <div className="flex items-center border-r" style={{ width: nameCol.width }}>
                                <div className="px-3">항목</div>
                                <div
                                    onMouseDown={nameCol.onMouseDown}
                                    className="ml-auto w-1 self-stretch cursor-col-resize hover:bg-gray-300"
                                    style={{ marginRight: -4 }}
                                    title="열 너비 조절"
                                />
                            </div>
                        </th>

                        <th className="px-3 py-2">착수</th>
                        <th className="px-3 py-2">완료</th>

                        <th className="px-3 py-2 text-right">
                            <div>계획</div>
                            <div className="text-xs text-gray-500">%</div>
                        </th>

                        <th className="px-3 py-2 text-right">
                            <div>확정(W-2주)</div>
                            <div className="text-xs text-gray-500">%</div>
                        </th>

                        {showWeek1Column && (
                            <th className="px-3 py-2 text-right">
                                <div>{week1ColumnLabel}</div>
                                <div className="text-xs text-gray-500">%</div>
                            </th>
                        )}

                        {showCurrentColumn && (
                            <th className="px-3 py-2 text-right">
                                <div>잠정(금주)</div>
                                <div className="text-xs text-gray-500">%</div>
                            </th>
                        )}

                        {/* <th className="px-3 py-2 text-right">
                                    <div>지난주 잠정</div>
                                    <div className="text-xs text-gray-500">%</div>
                                </th>
                                <th className="px-3 py-2 text-right">
                                    <div>지난주 확정</div>
                                    <div className="text-xs text-gray-500">%</div>
                                </th>
                                <th className="px-3 py-2 text-right">
                                    <div>델타</div>
                                    <div className="text-xs text-gray-500">%</div>
                                </th> */}

                        {showWeeklyInput && (
                            <th className="px-3 py-2">
                                <div>금주 입력</div>
                                <div className="text-xs text-gray-500">EV %</div>
                            </th>
                        )}

                        <th className="px-3 py-2">Status</th>
                        <th className="px-3 py-2">수행사</th>
                        <th className="px-3 py-2">리스크/태스크</th>
                    </tr>
                </thead>

                <tbody>
                    {visibleRows.map((r) => {
                        // const pv = plannedPercentAt(r, pvRefDate);
                        const pv = r.plannedValue ?? null;
                        // const hasChildren = treeIndex.childrenMap[r.id]?.length > 0;
                        const hasChildrenUi = (displayTreeIndex.childrenMap[r.id]?.length ?? 0) > 0;
                        const hasChildrenRollup = (rollupTreeIndex.childrenMap[r.id]?.length ?? 0) > 0;
                        // const confirmedEV = r.earnedValue ?? null; // 확정 EV
                        // 확정 EV: Leaf는 자기 값, Parent는 하위 leaf 합산(표시용)
                        const selfConfirmedEV = r.earnedValue ?? null;
                        const agg = aggregatedEvMap[r.id];
                        const confirmedEV = hasChildrenRollup
                            ? agg?.hasAny
                                ? Number(agg.pct?.toFixed(1))
                                : null
                            : selfConfirmedEV;

                        const f = F[r.id];
                        // const val = evInput[r.id] ?? '';
                        const isExpanded = expanded.has(r.id);

                        const draftEV = f?.draftEV ?? null;
                        const liveEV = currentWeekMap[r.id] ?? null;
                        const plannedStartDate = r.plannedStart ? parseISO(r.plannedStart) : null;
                        const plannedStarted = plannedStartDate ? plannedStartDate <= refFriday : false;

                        const displayPlan = pv;
                        const displayConfirmedW2 = week2ConfirmedMap[r.id] ?? null;
                        const displayWeek1 = (week1Pending ? week1DisplayMap[r.id] : week1ApprovedMap[r.id]) ?? null;

                        const displayProvisionalText =
                            showCurrentColumn
                                ? formatLiveProvisional({
                                      provisional: liveEV,
                                      confirmed: confirmedEV,
                                      plannedStarted,
                                  })
                                : null;

                        const kind = statusFromRow(r, pv, pvRefDate);
                        const rejectionNote = (f?.rejectionNote ?? '').trim() || null;
                        // const isApprovedLocked = F?.[r.id]?.statusForWeek === 'APPROVED';
                        const isCompleted = (confirmedEV ?? 0) >= 100;
                        // const canEditThisRowProgress =
                        //     !entryClosed &&
                        //     !isReadOnly &&
                        //     // policy.canEditField('actualProgress') &&
                        //     !hasChildrenRollup &&
                        //     !isCompleted;

                        // const resubmitAllowed = canResubmitAfterClose(r.id);
                        // const entryLocked = entryClosed && !resubmitAllowed;

                        return (
                            <React.Fragment key={r.id}>
                                <tr className="border-t //align-top">
                                    {/* 선택 칸 */}
                                    {/* <td className="px-3 py-2 w-10">
                                        {
                                            !hasChildrenRollup ? (
                                                <input
                                                    type="checkbox"
                                                    className="accent-gray-700"
                                                    checked={selectedIds.has(r.id)}
                                                    onChange={(e) => toggleSelect(r.id, e.target.checked)}
                                                />
                                            ) : null
                                        }
                                    </td> */}

                                    {/* 계층 코드 */}
                                    <td className={`px-3 py-2`}>{r.code}</td>

                                    {/* 항목 이름(트리 토글) */}
                                    <td className="px-0 py-2">
                                        <div className="flex items-start" style={{ width: nameCol.width }}>
                                            {/* 들여쓰기/트리 토글 */}
                                            <div style={{ width: (r.level - 1) * 16 }} />
                                            {hasChildrenUi ? (
                                                <button
                                                    type="button"
                                                    className="mt-0.5 mr-1 inline-flex w-5 h-5 items-center justify-center rounded hover:bg-gray-100"
                                                    title={isExpanded ? '접기' : '펼치기'}
                                                    onClick={() => toggleExpand(r.id)}
                                                >
                                                    <svg
                                                        width="12"
                                                        height="12"
                                                        viewBox="0 0 24 24"
                                                        className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                                    >
                                                        <path d="M8 5l8 7-8 7V5z" fill="currentColor" />
                                                    </svg>
                                                </button>
                                            ) : (
                                                <span className="w-5 h-5 mr-1" />
                                            )}

                                            {/* 이름 */}
                                            <div className="relative min-w-0 flex-1">
                                                <div className="flex items-start justify-between gap-2">
                                                    <button
                                                        type="button"
                                                        className="block min-w-0 flex-1 overflow-hidden whitespace-nowrap text-ellipsis text-left hover:underline"
                                                        onClick={() =>
                                                            setHistoryModal({
                                                                open: true,
                                                                row: r,
                                                                pv: r.plannedValue ?? null,
                                                                baseDate: pvRefDate,
                                                            })
                                                        }
                                                    >
                                                        {r.name}
                                                    </button>
                                                </div>
                                                <span
                                                    aria-hidden
                                                    className="pointer-events-none absolute right-0 top-0 h-full w-10 bg-gradient-to-l from-white to-transparent"
                                                />
                                                <span
                                                    aria-hidden
                                                    className="pointer-events-none absolute right-1 top-0 h-full flex items-center text-gray-400"
                                                />
                                            </div>
                                        </div>
                                    </td>

                                    {/* 기본 메타 */}

                                    <td className="px-3 py-2">{r.plannedStart ?? '-'}</td>
                                    <td className="px-3 py-2">{r.plannedFinish ?? '-'}</td>
                                    <td className="px-3 py-2 text-right">{formatPct(displayPlan)}</td>

                                    <td className="px-3 py-2 text-right">
                                        <span>{formatPct(displayConfirmedW2)}</span>
                                    </td>

                                    {showWeek1Column && (
                                        <td className="px-3 py-2 text-right">
                                            <span>{formatPct(displayWeek1)}</span>
                                        </td>
                                    )}

                                    {showCurrentColumn && (
                                        <td className="px-3 py-2 text-right">
                                            <span>{displayProvisionalText}</span>
                                        </td>
                                    )}

                                    {/* <td className="px-3 py-2 text-right">
                                                <span>{formatPct(lastProv)}</span>
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                <span>{formatPct(lastAppr)}</span>
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                <span>
                                                    {lastDeltaPct != null
                                                        ? `${Math.round(lastDeltaPct * 10) / 10}%`
                                                        : '-'}
                                                </span>
                                            </td> */}

                                    {/* 입력(주간) — approver/progress */}
                                    {/* {showWeeklyInput && (
                                        <td className="px-3 py-2">
                                            <div className="space-y-1">
                                                {canEditThisRowProgress ? (
                                                    <div className="flex items-center gap-2">
                                                        <div className="relative">
                                                            <input
                                                                type="number"
                                                                min={0}
                                                                max={100}
                                                                step={0.1}
                                                                inputMode="decimal"
                                                                className={[
                                                                    'w-24 sm:w-24 md:w-20',
                                                                    'text-right text-sm',
                                                                    'rounded-lg px-2 py-1 pr-7',
                                                                    'border',
                                                                    'appearance-none',
                                                                    '[&::-webkit-outer-spin-button]:appearance-none',
                                                                    '[&::-webkit-inner-spin-button]:appearance-none',
                                                                    '[-moz-appearance:textfield]',
                                                                    isApprovedLocked
                                                                        ? 'bg-gray-50 text-gray-400 cursor-not-allowed border-gray-200'
                                                                        : 'bg-white border-gray-300',
                                                                    'focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400',
                                                                ].join(' ')}
                                                                value={val}
                                                                disabled={entryLocked || isApprovedLocked}
                                                                onChange={(e) => {
                                                                    if (isApprovedLocked) return;
                                                                    const v = e.target.value;
                                                                    setEvInput((prev) => ({ ...prev, [r.id]: v }));
                                                                    const base = baselineEvRef.current.get(r.id) ?? '';
                                                                    const isDirty = normalizeEvInput(v) !== base;

                                                                    setDirtyIds((prev) => {
                                                                        const next = new Set(prev);
                                                                        if (isDirty) next.add(r.id);
                                                                        else next.delete(r.id);
                                                                        return next;
                                                                    });

                                                                    setSelectedIds((prev) => {
                                                                        const next = new Set(prev);
                                                                        next.add(r.id);
                                                                        return next;
                                                                    });
                                                                }}
                                                            />
                                                            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                                                                %
                                                            </span>
                                                        </div>

                                                        <span
                                                            className="ml-1 inline-flex w-3 items-center justify-center"
                                                            aria-hidden
                                                        >
                                                            <span
                                                                className={[
                                                                    'inline-block rounded-full bg-amber-500',
                                                                    'w-2.5 h-2.5',
                                                                    'transition-opacity',
                                                                    dirtyIds.has(r.id) ? 'opacity-100' : 'opacity-0',
                                                                ].join(' ')}
                                                                title="변경됨 (미저장)"
                                                                aria-label="변경됨 (미저장)"
                                                            />
                                                        </span>
                                                    </div>
                                                ) : isCompleted ? (
                                                    <span className="text-xs text-gray-500">완료</span>
                                                ) : draftEV != null ? (
                                                    <span className="text-xs text-gray-700">{draftEV}%</span>
                                                ) : (
                                                    <span className="text-xs text-gray-400">-</span>
                                                )}

                                                {rejectionNote && (
                                                    <div className="whitespace-pre-wrap text-[11px] leading-4 text-rose-600">
                                                        반려: {rejectionNote}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    )} */}
                                    {showWeeklyInput && (
                                        <td className="px-3 py-2">
                                            <div className="space-y-1">
                                                {isCompleted ? (
                                                    <span className="text-xs text-gray-500">완료</span>
                                                ) : draftEV != null ? (
                                                    <span className="text-xs text-gray-700">{draftEV}%</span>
                                                ) : (
                                                    <span className="text-xs text-gray-400">-</span>
                                                )}

                                                {rejectionNote && (
                                                    <div className="whitespace-pre-wrap text-[11px] leading-4 text-rose-600">
                                                        반려: {rejectionNote}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    )}

                                    {/* Status + 주간칩 */}
                                    <td className="px-3 py-2">
                                        <div className="flex items-center gap-2">
                                            <StatusBadge kind={kind} />
                                            {evViewMode === 'LIVE' &&
                                                (() => {
                                                    const s = r.plannedStart ? parseISO(r.plannedStart) : null;
                                                    const plannedStartOK = s ? s <= refFriday : false;
                                                    const hasSubmission = !!f?.hasSubmissionForWeek;
                                                    const shouldShow = plannedStartOK || hasSubmission;
                                                    if (!shouldShow) return null;

                                                    return (
                                                        <span
                                                            title={
                                                                rejectionNote
                                                                    ? `반려 사유: ${rejectionNote}`
                                                                    : undefined
                                                            }
                                                            className="inline-flex items-center gap-1"
                                                        >
                                                            <WbsProgressChip status={f?.statusForWeek ?? null} />
                                                            {rejectionNote && (
                                                                <button
                                                                    type="button"
                                                                    className="text-xs underline text-rose-600 hover:text-rose-700"
                                                                    onClick={() =>
                                                                        setNoteModal({
                                                                            open: true,
                                                                            note: rejectionNote,
                                                                        })
                                                                    }
                                                                >
                                                                    사유
                                                                </button>
                                                            )}
                                                        </span>
                                                    );
                                                })()}
                                        </div>
                                    </td>

                                    {/* 수행사 */}
                                    <td className="px-3 py-2">{r.executer ?? '-'}</td>

                                    {/* 리스크/태스크 */}
                                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <button
                                                className="text-[11px] rounded px-2 py-0.5 border bg-gray-50 hover:bg-gray-100"
                                                onClick={() =>
                                                    onOpenLinks?.('risk', {
                                                        id: r.id,
                                                        nodeId: r.nodeId!,
                                                        code: r.code,
                                                        name: r.name,
                                                    })
                                                } // [STR-ID]
                                                title="연결된 리스크 보기/추가"
                                            >
                                                리스크 {counts?.[r.id]?.risks ?? 0}
                                            </button>
                                            <button
                                                className="text-[11px] rounded px-2 py-0.5 border bg-gray-50 hover:bg-gray-100"
                                                onClick={() =>
                                                    onOpenLinks?.('task', {
                                                        id: r.id,
                                                        nodeId: r.nodeId!,
                                                        code: r.code,
                                                        name: r.name,
                                                    })
                                                } // [STR-ID]
                                                title="연결된 태스크 보기/추가"
                                            >
                                                태스크 {counts?.[r.id]?.tasks ?? 0}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </React.Fragment>
                        );
                    })}

                    {visibleRows.length === 0 && (
                        <tr>
                            <td colSpan={COL_COUNT} className="px-3 py-6 text-center text-gray-500">
                                항목이 없습니다.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {/* 상세 모달 */}
            <WbsProgressHistoryModal
                open={historyModal.open}
                onClose={() => setHistoryModal({ open: false, row: null, pv: null, baseDate: null })}
                projectId={projectId}
                row={historyModal.row}
            />

            {/* 반려 사유 보기 모달 */}
            <Modal open={noteModal.open} title="반려 사유" onClose={() => setNoteModal({ open: false, note: null })}>
                <div className="text-sm whitespace-pre-wrap">{noteModal.note}</div>
                <div className="mt-4 text-right">
                    <button
                        className="text-xs rounded border px-2.5 py-1 hover:bg-gray-50"
                        onClick={() => setNoteModal({ open: false, note: null })}
                    >
                        닫기
                    </button>
                </div>
            </Modal>
        </section>
    );
}
